// The player's game settings, kept on this device and shared by every mode:
// the play menu's Settings section (docs/03-Experience/Site Map.md,
// docs/02-Game-Design/Apogee Mode.md). Missing or unreadable storage reads as the defaults.
import { createStore } from '../store';

export const GAME_SETTINGS_KEY = 'wits.settings';

export type Lettercase = 'upper' | 'lower';

export interface GameSettings {
  /** Apogee: each lettered star's point value under it. */
  showStarValues: boolean;
  /** Every mode: the case of the letters in the star glyphs and the word in progress. */
  lettercase: Lettercase;
}

export const DEFAULT_SETTINGS: GameSettings = { showStarValues: false, lettercase: 'upper' };

export function readSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(GAME_SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<GameSettings>;
    return {
      showStarValues: typeof parsed.showStarValues === 'boolean' ? parsed.showStarValues : DEFAULT_SETTINGS.showStarValues,
      lettercase: parsed.lettercase === 'upper' || parsed.lettercase === 'lower' ? parsed.lettercase : DEFAULT_SETTINGS.lettercase,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export const gameSettings = createStore<GameSettings>(readSettings());

export function setSetting<K extends keyof GameSettings>(key: K, value: GameSettings[K]): void {
  gameSettings.set((s) => ({ ...s, [key]: value }));
  try {
    localStorage.setItem(GAME_SETTINGS_KEY, JSON.stringify(gameSettings.get()));
  } catch {
    // Private mode or blocked storage: the setting lasts until the page reloads.
  }
}

/** Letters as the player reads them: the levels keep them uppercase. */
export function inCase(text: string, lettercase: Lettercase): string {
  return lettercase === 'lower' ? text.toLowerCase() : text;
}
